import { useEffect, useRef, useState } from 'react';
import { useLatest, useMemoizedFn } from 'ahooks';
import { useMutation, useQueryClient } from '@zg-rentals/react-query5';
import { ZDocsUploadManager } from '@zillow/zdocs-web';
import { DatarouterError } from '@zg-rentals/web-base';
import { CappsApplicationsWebApiHandler_getRentalApplication } from '@zg-rentals/ts-capps-client';
import {
  CappsApplicationsWebApiHandler_getRentalApplication_queryOptions,
  useCappsRenterDocumentsWebApiHandler_addIncomeDocuments,
  useCappsRenterDocumentsWebApiHandler_requestUserToken,
} from '@zg-rentals/ts-capps-client-react-query';
import type { Answers } from '../models/rental-application';
import { DOCUMENT_POOL_TAG } from '../constants/applicationsConstants';
import { getMaxDocumentsPerIncome } from '../constants/form';
import { useIsBaseApplicationChangesEnabled } from '../utils/ab-tests';
import { RENTAL_APPLICATION_TAG } from './useRentalApplication';

export const INCOME_DOCUMENT_UPLOAD_KEY = ['incomeDocumentUpload'];

export const MAX_UPLOAD_SIZE_BYTES = 100 * 1024 * 1024;

export const ACCEPTED_FILE_EXTENSIONS = ['.pdf', '.jpeg', '.jpg', '.png'];

export const STALLED_UPLOAD_MS = 60_000;

const ACCEPTED_MIME_TYPES = ['application/pdf', 'image/jpeg', 'image/png'];

function isAcceptedFile(file: File) {
  const name = file.name.toLowerCase();
  return (
    ACCEPTED_MIME_TYPES.includes(file.type) || ACCEPTED_FILE_EXTENSIONS.some((extension) => name.endsWith(extension))
  );
}

export type PendingUpload = {
  key: string;
  incomeId: string;
  fileName: string;
  savedIds?: Array<string>;
};

export type FailedUploadReason = 'type' | 'tooLarge' | 'limit' | 'upload' | 'save';

export type FailedUpload = {
  key: string;
  incomeId: string;
  fileName: string;
  file: File;
  reason: FailedUploadReason;
};

type Entry = { key: string; file: File; fileName: string };

type UploadedFiles = Awaited<ReturnType<ZDocsUploadManager['uploadFiles']>>;

let nextKey = 0;

type IncomeDocumentIds = {
  currentIncomes?: Array<{ id?: string | null; documents?: Array<{ id?: string | null } | null> | null } | null> | null;
};

function getContainerIds(answers: IncomeDocumentIds | null | undefined, incomeId: string) {
  const income = (answers?.currentIncomes ?? []).find((current) => current?.id === incomeId);
  return new Set((income?.documents ?? []).flatMap((document) => (document?.id ? [document.id] : [])));
}

function isListed(answers: IncomeDocumentIds | null | undefined, { incomeId, savedIds }: PendingUpload) {
  const containerIds = getContainerIds(answers, incomeId);
  return !!savedIds && savedIds.every((id) => containerIds.has(id));
}

// A saved file counts from the moment its save returns, before the refetched application lists it
function getHeldDocumentCount(
  answers: IncomeDocumentIds | null | undefined,
  pending: Array<PendingUpload>,
  incomeId: string,
) {
  const ids = getContainerIds(answers, incomeId);
  let uploading = 0;
  for (const entry of pending) {
    if (entry.incomeId !== incomeId) {
      continue;
    }
    if (entry.savedIds) {
      entry.savedIds.forEach((id) => ids.add(id));
    } else {
      uploading += 1;
    }
  }
  return ids.size + uploading;
}

/**
 * Uploads income documents for the Income documents step through one zDocs upload manager, and
 * stores each file in the container of its income source. A pending entry lasts until the
 * refetched application lists the saved file. Failures stay in the state for the step to show.
 * Every batch runs as a mutation with INCOME_DOCUMENT_UPLOAD_KEY, so the wizard can block
 * navigation while one runs
 */
export function useIncomeDocumentUploads(applicationId: string, answers: Answers | null | undefined) {
  const queryClient = useQueryClient();
  const { mutateAsync: requestUserToken } = useCappsRenterDocumentsWebApiHandler_requestUserToken({
    meta: { context: { api: 'cappsRenterDocuments', details: 'requestUserToken' } },
  });
  const { mutateAsync: addIncomeDocuments } = useCappsRenterDocumentsWebApiHandler_addIncomeDocuments({
    meta: {
      context: { api: 'cappsRenterDocuments', details: 'addIncomeDocuments' },
      invalidates: [RENTAL_APPLICATION_TAG, DOCUMENT_POOL_TAG],
    },
  });
  const [pending, setPending] = useState<Array<PendingUpload>>([]);
  const [failed, setFailed] = useState<Array<FailedUpload>>([]);
  const [prunedForAnswers, setPrunedForAnswers] = useState(answers);
  const latestAnswers = useLatest(answers);
  const maxDocumentsPerIncome = getMaxDocumentsPerIncome(useIsBaseApplicationChangesEnabled());
  const managerRef = useRef<ZDocsUploadManager | null>(null);
  const claimedIdsRef = useRef(new Set<string>());
  const uploadActivityRef = useRef(() => {});

  if (answers !== prunedForAnswers) {
    setPrunedForAnswers(answers);
    setPending((current) => current.filter((entry) => !isListed(answers, entry)));
  }

  useEffect(() => () => managerRef.current?.disconnect(), []);

  const getManager = () => {
    managerRef.current ??= new ZDocsUploadManager({
      generateUploadToken: async () => (await requestUserToken()).userToken ?? '',
      onChange: () => uploadActivityRef.current(),
      onProgress: () => uploadActivityRef.current(),
    });
    return managerRef.current;
  };

  // The manager has no timeout, so a batch with no upload activity fails, and the next batch gets a new manager
  const uploadFiles = (files: Array<File>) => {
    const manager = getManager();
    return new Promise<UploadedFiles>((resolve) => {
      let timer: ReturnType<typeof setTimeout> | undefined;
      let isDone = false;
      const finish = (uploads: UploadedFiles) => {
        if (isDone) {
          return;
        }
        isDone = true;
        clearTimeout(timer);
        uploadActivityRef.current = () => {};
        resolve(uploads);
      };
      uploadActivityRef.current = () => {
        clearTimeout(timer);
        timer = setTimeout(() => {
          manager.disconnect();
          if (managerRef.current === manager) {
            managerRef.current = null;
          }
          finish([]);
        }, STALLED_UPLOAD_MS);
      };
      uploadActivityRef.current();
      manager.uploadFiles(files).then(finish, () => finish([]));
    });
  };

  const markSaved = (key: string, savedIds: Array<string>) => {
    savedIds.forEach((id) => claimedIdsRef.current.add(id));
    setPending((current) =>
      current.flatMap((entry) => {
        if (entry.key !== key) {
          return [entry];
        }
        const saved = { ...entry, savedIds };
        return isListed(latestAnswers.current, saved) ? [] : [saved];
      }),
    );
  };

  const markFailed = (incomeId: string, entry: Entry, reason: FailedUploadReason) => {
    setPending((current) => current.filter(({ key }) => key !== entry.key));
    setFailed((current) => [...current, { ...entry, incomeId, reason }]);
  };

  // Not through the query cache, so a failed check cannot put the page's application query in error
  const findStoredDocument = async (incomeId: string, knownIds: Set<string>) => {
    const application = await CappsApplicationsWebApiHandler_getRentalApplication({ id: applicationId });
    queryClient.setQueryData(
      CappsApplicationsWebApiHandler_getRentalApplication_queryOptions({ id: applicationId }).queryKey,
      application,
    );
    return [...getContainerIds(application.answers, incomeId)].find(
      (id) => !knownIds.has(id) && !claimedIdsRef.current.has(id),
    );
  };

  // Batches and saves run one at a time, so an unclaimed new id can only come from the save that just failed
  const { mutate: uploadBatch } = useMutation({
    mutationKey: INCOME_DOCUMENT_UPLOAD_KEY,
    scope: { id: 'incomeDocumentUpload' },
    meta: { context: { api: 'cappsRenterDocuments', details: 'uploadIncomeDocuments' } },
    mutationFn: async ({ incomeId, entries }: { incomeId: string; entries: Array<Entry> }) => {
      const uploads = await uploadFiles(entries.map(({ file }) => file));

      for (const entry of entries) {
        const messageToken = uploads.find(({ file }) => file === entry.file)?.messageToken;
        if (!messageToken) {
          markFailed(incomeId, entry, 'upload');
          continue;
        }

        const knownIds = new Set([...getContainerIds(latestAnswers.current, incomeId), ...claimedIdsRef.current]);
        try {
          const saved = await addIncomeDocuments({ postData: { incomeId, documents: [{ messageToken }] } });
          const savedIds = (saved ?? []).map(({ documentId }) => documentId);
          if (savedIds.length > 0) {
            markSaved(entry.key, savedIds);
          } else {
            markFailed(incomeId, entry, 'save');
          }
          continue;
        } catch (error) {
          // capps rejects a 4xx before it stores anything
          if (DatarouterError.is(error) && error.responseStatus >= 400 && error.responseStatus < 500) {
            markFailed(incomeId, entry, 'save');
            continue;
          }
          // Any other failed save can still have stored the file, so the refetched application decides
        }

        const storedId = await findStoredDocument(incomeId, knownIds).catch(() => undefined);
        if (storedId) {
          markSaved(entry.key, [storedId]);
        } else {
          markFailed(incomeId, entry, 'save');
        }
      }
    },
  });

  const addFiles = useMemoizedFn((incomeId: string, files: Array<File>) => {
    let room = Math.max(maxDocumentsPerIncome - getHeldDocumentCount(answers, pending, incomeId), 0);
    const accepted: Array<Entry> = [];

    for (const file of files) {
      const entry = { key: `upload-${(nextKey += 1)}`, file, fileName: file.name.replace(/\.[^.]+$/, '') };
      if (!isAcceptedFile(file)) {
        setFailed((current) => [...current, { ...entry, incomeId, reason: 'type' }]);
      } else if (file.size > MAX_UPLOAD_SIZE_BYTES) {
        setFailed((current) => [...current, { ...entry, incomeId, reason: 'tooLarge' }]);
      } else if (room === 0) {
        setFailed((current) => [...current, { ...entry, incomeId, reason: 'limit' }]);
      } else {
        room -= 1;
        accepted.push(entry);
      }
    }

    if (accepted.length > 0) {
      setPending((current) => [...current, ...accepted.map(({ key, fileName }) => ({ key, incomeId, fileName }))]);
      uploadBatch({ incomeId, entries: accepted });
    }
  });

  const hasRoom = (incomeId: string) => getHeldDocumentCount(answers, pending, incomeId) < maxDocumentsPerIncome;

  return { pending, failed, addFiles, hasRoom };
}
